/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then, When } from "react-if";
import { Box, FlexContainer, Switch, Text, theme } from "@nordcloud/gnui";
import {
  BatchAction,
  BatchedEventAction,
  EventAction,
  EventActionAttempt,
  EventActionBatch,
  EventActionScope,
  useGetEventActionResourceStateSearchQuery,
  useGetGlobalEventActionResourceStateQuery,
} from "~/generated/graphql";
import { NoData } from "~/components";
import { QuerySearch } from "~/components/SearchInput/types";
import {
  useElementNavigation,
  useGetSearchPhrase,
  useQueryState,
} from "~/hooks";
import { isNonEmpty, sortItem } from "~/tools";
import { getFlatEventEntities } from "~/utils";
import { buildErrorCodeCountsByActionId } from "~/views/events/components/EventItem.utils";
import { useEvent } from "~/views/events/EventsDetails/EventProvider";
import { ReportView } from "~/views/events/EventsDetails/Tabs/Details/components/ReportView/ReportView";
import { isNotFinishedExecution } from "~/views/events/EventsDetails/Tabs/Details/components/utils";
import {
  isResourceScope,
  shouldAwaitResourceStates,
  hasEventActionResources,
  getLatestAttemptSummaryId,
} from "~/views/events/helpers";
import { WrapMax } from "~/views/plans/styled";
import { EventActionBox } from "./components/ActionBox/EventActionBox";
import { EventActionBatchItem } from "./components/EventActionBatchItem";
import { EventEntityItem } from "./components/EventEntityItem";
import { EventDetailsTabCtxProvider } from "./EventDetailsTabProvider";
import { useEventResourceController } from "./hooks/useEventResourceController";
import type { EventResourceGroup } from "./hooks/useEventResourceController/types";

export function EventDetailsTab() {
  const { event: eventCtx } = useEvent();

  const actions = eventCtx.actions;
  const actionBatches = eventCtx.actionBatches;

  const hasEntities = isNonEmpty(actions) || isNonEmpty(actionBatches);

  if (hasEntities) {
    return EventActionDetailsTab();
  }

  return <NoData hasIcon message="There are no Actions" />;
}

function EventActionDetailsTab() {
  const { event: eventCtx } = useEvent();
  const actions = eventCtx.actions;
  const batches = eventCtx.actionBatches;

  const [attempt, setAttempt] = useState<EventActionAttempt>();
  const [isReportView, setIsReportView] = useState(false);
  const {
    queryState: resourceSearchState,
    updateQueryState: updateResourceSearchState,
    removeQueryState: removeResourceSearchState,
  } = useQueryState<QuerySearch>();
  const { currentHash, updateHash, scrollToElement } = useElementNavigation();
  const [resourceStateGroups, setResourceStateGroups] = useState<
    EventResourceGroup[] | undefined
  >(undefined);
  const [totalResourceStates, setTotalResourceStates] = useState(0);

  const resetResourceStates = useCallback(() => {
    setResourceStateGroups(undefined);
    setTotalResourceStates(0);
  }, []);

  const sortedItems = useMemo(() => {
    const batchItems = batches.map((batch: EventActionBatch) => ({
      ...batch,
      actions: batch.actions.map((action: BatchedEventAction) => ({
        ...action,
        scope: EventActionScope.Resource,
      })),
    }));
    const actionItems = actions.map((action: EventAction) => ({
      ...action,
    }));
    return sortItem(
      [...batchItems, ...actionItems],
      (a, b) => a.order - b.order
    );
  }, [batches, actions]);

  const flatEntities = useMemo(
    () => getFlatEventEntities(sortedItems),
    [sortedItems]
  );

  const [eventEntityId, setEventEntityId] = useState<string>(sortedItems[0].id);
  const eventEntity = useMemo(() => {
    const flatMatch = flatEntities.find(
      (entity) => entity.id === eventEntityId
    );
    if (flatMatch) {
      return flatMatch;
    }
    return (
      sortedItems.find((item) => item.id === eventEntityId) ?? sortedItems[0]
    );
  }, [flatEntities, eventEntityId, sortedItems]);
  const eventEntityStatus =
    attempt?.state?.status ?? eventEntity.globalState?.status;

  const hasResources = useMemo(
    () => hasEventActionResources(eventEntity),
    [eventEntity]
  );

  const errorCodeCountsByActionId = useMemo(
    () =>
      buildErrorCodeCountsByActionId({
        actions: eventCtx.actions,
        actionBatches: eventCtx.actionBatches,
      }),
    [eventCtx.actions, eventCtx.actionBatches]
  );

  const selectedActionErrorCodeCounts =
    errorCodeCountsByActionId[getLatestAttemptSummaryId(eventEntity)] ?? [];

  const { phrase: searchPhraseForQuery } = useGetSearchPhrase(
    resourceSearchState.searchPhrase
  );

  // When no attempt is selected, we want to reflect the latest attempt in the global list.
  const latestAttemptId = getLatestAttemptSummaryId(eventEntity);

  const queryVariables = useMemo(() => {
    return {
      limit: Number(resourceSearchState.limit),
      filter: {
        phrase: searchPhraseForQuery,
      },
    };
  }, [resourceSearchState.limit, searchPhraseForQuery]);

  // Global query (merged latest per resource).
  const globalResourceStatesQuery = useGetGlobalEventActionResourceStateQuery({
    variables: {
      ...queryVariables,
      page: undefined,
      eventId: eventCtx.id,
      eventActionId: eventEntity.id,
      excludeGroupStatus: isNotFinishedExecution(eventEntityStatus),
    },
    fetchPolicy: "network-only",
    skip: !!attempt || !isResourceScope(eventEntity),
  });

  // Per-attempt search query
  const attemptResourceStatesQuery = useGetEventActionResourceStateSearchQuery({
    variables: {
      ...queryVariables,
      eventId: eventCtx.id,
      eventActionId: attempt?.id ?? latestAttemptId,
      excludeGroupStatus: isNotFinishedExecution(eventEntityStatus),
    },
    fetchPolicy: "network-only",
    skip: !attempt || !isResourceScope(eventEntity),
  });

  // Use data from the active query
  const resourceStatesQueryApi = attempt
    ? attemptResourceStatesQuery
    : globalResourceStatesQuery;

  const resourceStatesData = attempt
    ? attemptResourceStatesQuery.data?.eventActionResourceStateSearch
    : globalResourceStatesQuery.data?.globalEventActionResourceState;

  const {
    loading: isLoadingResourceStates,
    startPolling,
    stopPolling,
  } = resourceStatesQueryApi;

  const refetchResourceStates = useCallback(
    async (limit?: number) => {
      const params = {
        eventId: eventCtx.id,
        eventActionId: attempt?.id ?? eventEntity.id,
        limit: limit ?? queryVariables.limit,
        filter: queryVariables.filter,
      };

      // Extract and update state
      const queryData = attempt
        ? (await attemptResourceStatesQuery.refetch(params)).data
            .eventActionResourceStateSearch
        : (await globalResourceStatesQuery.refetch(params)).data
            .globalEventActionResourceState;

      if (queryData) {
        setTotalResourceStates(queryData.pageInfo.count);
        setResourceStateGroups(queryData.result ?? []);
      }
    },
    [
      eventCtx.id,
      attempt,
      eventEntity.id,
      queryVariables.limit,
      queryVariables.filter,
      attemptResourceStatesQuery,
      globalResourceStatesQuery,
    ]
  );

  useEffect(() => {
    if (isLoadingResourceStates || !resourceStatesData) {
      return;
    }

    setTotalResourceStates(resourceStatesData.pageInfo.count);
    setResourceStateGroups(resourceStatesData.result ?? []);
  }, [resourceStatesData, isLoadingResourceStates]);

  useEffect(() => {
    const {
      action: hashActionId,
      batch: hashBatchId,
      reportView,
      tab,
    } = currentHash || {};

    const hashEntityId = hashActionId || hashBatchId;

    if (hashEntityId && (!tab || tab === "details")) {
      if (
        flatEntities.some((item) => item.id === hashActionId) ||
        batches.some((batch: EventActionBatch) => batch.id === hashBatchId)
      ) {
        setEventEntityId(hashEntityId);
        const targetElementId = hashActionId
          ? `action-${hashActionId}`
          : `batch-${hashBatchId}`;
        scrollToElement(targetElementId);
      } else {
        // invalid id in hash - reset to first item
        setEventEntityId(sortedItems[0].id);
        updateHash({ action: null, batch: null }, { merge: true });
      }
    }

    if (reportView !== "true") {
      updateHash({ reportView: null }, { merge: true });
      setIsReportView(false);
    } else if (reportView === "true" && !isReportView) {
      setIsReportView(true);
    }
  }, [
    currentHash,
    setIsReportView,
    scrollToElement,
    flatEntities,
    batches,
    sortedItems,
    updateHash,
  ]);

  const resourceController = useEventResourceController({
    resourceStateGroups,
    resourceSearchState,
    isLoadingResourceStates,
    updateResourceSearchState,
    refetchResourceStates,
    removeResourceSearchState,
    hasResources,
  });
  const { clearSearch } = resourceController;

  const switchEventEntity = useCallback(
    (nextId: string, options: { resetAttempt?: boolean } = {}) => {
      if (nextId === eventEntityId) {
        return;
      }

      resetResourceStates();
      setEventEntityId(nextId);
      if (options.resetAttempt) {
        setAttempt(undefined);
      }
      clearSearch();
      const newHash = options.resetAttempt
        ? { batch: nextId, action: null }
        : { action: nextId, batch: null };
      updateHash(newHash, { merge: true });
    },
    [
      eventEntityId,
      resetResourceStates,
      setEventEntityId,
      setAttempt,
      clearSearch,
    ]
  );

  const handleSelectAttempt = async (
    actionAttempt: EventActionAttempt | undefined
  ) => {
    if (actionAttempt?.id !== attempt?.id) {
      resetResourceStates();
    }

    setAttempt(actionAttempt);
  };

  const eventEntityItemProps = {
    ...{
      attempt,
      eventEntity,
      handleSelectAttempt,
    },
  };

  const awaitResourceStates = useCallback(() => {
    return shouldAwaitResourceStates(eventEntity, resourceStateGroups);
  }, [eventEntity, resourceStateGroups]);

  useEffect(() => {
    if (awaitResourceStates()) {
      startPolling(2000);
    } else {
      stopPolling();
    }
  }, [awaitResourceStates, startPolling, stopPolling, resourceStateGroups]);

  return (
    <Box innerSpacing="spacing00">
      <EventDetailsTabCtxProvider
        value={{
          eventAction: eventEntity,
          attempt,
          setAttempt,
          resourceController,
        }}
      >
        <Row>
          <Col xs={8}>
            <FlexContainer
              direction="row"
              justifyContent="space-between"
              padding={`${theme.spacing.spacing04} 0`}
            >
              <div>
                <Text width="100%" align="left" size="md" weight="medium">
                  Actions
                </Text>
              </div>
              <div>
                <When condition={isResourceScope(eventEntity)}>
                  <Switch
                    name="label-left"
                    position="left"
                    labelText="Report View"
                    onChange={() => {
                      if (isReportView) {
                        removeResourceSearchState(["searchPhrase"]);
                      }
                      updateHash(
                        { reportView: isReportView ? null : "true" },
                        { merge: true }
                      );
                      setIsReportView(!isReportView);
                    }}
                    checked={isReportView}
                    disabled={!isResourceScope(eventEntity)}
                  />
                </When>
              </div>
            </FlexContainer>
          </Col>
        </Row>
        <Row>
          <Col xs={4} sm={4} md={4} lg={3}>
            <FlexContainer direction="column" justifyContent="flex-start">
              {sortedItems.map((item) => (
                <If
                  condition={"actions" in item && !!item.actions}
                  key={item.id}
                >
                  <Then>
                    <WrapMax css={{ paddingTop: `${theme.spacing.spacing03}` }}>
                      <EventActionBatchItem
                        actionBatch={item}
                        active={eventEntity?.id === item.id}
                        onClick={() =>
                          switchEventEntity(item.id, { resetAttempt: true })
                        }
                      >
                        {"actions" in item &&
                          item.actions?.map((batchAction: BatchAction) => (
                            <EventEntityItem
                              key={batchAction.id}
                              item={batchAction}
                              onMouseDown={() =>
                                switchEventEntity(batchAction.id)
                              }
                              flatEventEntities={flatEntities}
                              errorCodeCounts={
                                errorCodeCountsByActionId[
                                  getLatestAttemptSummaryId(batchAction)
                                ] ?? []
                              }
                              {...eventEntityItemProps}
                            />
                          ))}
                      </EventActionBatchItem>
                    </WrapMax>
                  </Then>

                  <Else>
                    <WrapMax
                      css={{
                        width: `calc(100% - ${theme.spacing.spacing04})`,
                      }}
                    >
                      <EventEntityItem
                        key={item.id}
                        item={item}
                        onMouseDown={() => switchEventEntity(item.id)}
                        flatEventEntities={flatEntities}
                        errorCodeCounts={
                          errorCodeCountsByActionId[
                            getLatestAttemptSummaryId(item)
                          ] ?? []
                        }
                        {...eventEntityItemProps}
                      />
                    </WrapMax>
                  </Else>
                </If>
              ))}
            </FlexContainer>
          </Col>
          <Col xs={4} sm={8} md={8} lg={9}>
            <Box boxStyle="grey" innerSpacing="spacing04">
              <FlexContainer alignItems="flex-start">
                <If condition={isReportView && isResourceScope(eventEntity)}>
                  <Then>
                    <ReportView
                      eventId={eventCtx.id}
                      eventActionId={eventEntity.id}
                      attempt={attempt}
                    />
                  </Then>
                  <Else>
                    <EventActionBox
                      eventEntity={eventEntity}
                      planId={eventCtx?.plan?.id}
                      attempt={attempt}
                      totalCount={totalResourceStates}
                      errorCodeCounts={selectedActionErrorCodeCounts}
                    />
                  </Else>
                </If>
              </FlexContainer>
            </Box>
          </Col>
        </Row>
      </EventDetailsTabCtxProvider>
    </Box>
  );
}
